import { ReactElement, useEffect, useState } from "react";
import { T } from "react-translator-component";
import { Box } from "@mui/material";
import LoadingModal from "../../../components/LoadingModal";
import { useAppDispatch } from "../../../redux/store";
import { setErrorObj } from "../../redux/errorReducer";
import apiServices from "../../services/apiServices";
import Modal, { sxTitle } from "../DisplayModal";
import GroupIcon from "./GroupIcon";
import MemberGroupDetails from "./MemberGroupDetails";
import MemberGroupUsers from "./MemberGroupUsers";

export type ModalType = "Details" | "Users" | "" | undefined | null
type InnerModalType = ModalType | "DetailsUsers"

interface Props {
  type: ModalType;
  id: number;
  onClose: () => void;
}

export default function DisplayModal({type, id, onClose}: Props): ReactElement {
  const dispatch = useAppDispatch();
  
  /* データ */
  const [memberGroup, setMemberGroup] = useState<any>();
  const [memberGroupUsers, setMemberGroupUsers] = useState<any[]>([]);
  const [banner, setBanner] = useState<string | undefined>(undefined);
  const [icon, setIcon] = useState<string | undefined>(undefined);

  /* 画面制御 */
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modalType, setModalType] = useState<InnerModalType>();

  useEffect(() => {
    if (memberGroup) return

    // MemberGroupDetailsではなくこのコンポーネントでデータを取得する。
    // - ユーザー一覧から詳細に戻った際にデータの再取得が発生しないようにする
    // - ダイアログ切替時のアニメーションの違和感をなくす
    const apiCalls = async () => {
      setIsLoading(true);
      try {
        const [m, u] = await Promise.all([apiServices.getMemberGroup(id), apiServices.getMemberGroupUsers(id)]);
        setMemberGroup(m.member_group);
        setMemberGroupUsers(u.member_group_users);
      } catch (error: any) {
        dispatch(setErrorObj(error));
      } finally {
        setIsLoading(false);
      }
    }
    apiCalls();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!memberGroup) return;
    if (memberGroup.banner_image && banner === undefined) {
      apiServices.getMemberGroupBanner(memberGroup.id)
        .then((blob: Blob) => setBanner(URL.createObjectURL(blob)))
        .catch(() => setBanner('')); // 再取得はさせない
    }
    if (icon === undefined) {
      apiServices.getMemberGroupIcon(memberGroup.id)
        .then((blob: Blob) => setIcon(URL.createObjectURL(blob)))
        .catch(() => setIcon('')); // 再取得はさせない
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberGroup]);

  useEffect(() => {
    setModalType(type);
  }, [type]);

  const modal = () => {
    if (!memberGroup) {
      return (<></>);
    }
    switch(modalType) {
      case "Details": {
        return (
          <Modal
            title={`${T("mng_member_groups_details")}`}
            open={true}
            secondButton={{
              label: T("mng_member_groups_users"),
              onClick: () => setModalType("DetailsUsers")
            }}
            onClose={onClose}
            sx={{ ...styles.container.base, ...styles.container.info }}
          >
            <MemberGroupDetails member_group={memberGroup} banner={banner} icon={icon} />
          </Modal>
        );
      }
      case "DetailsUsers": {
        return (
          <Modal
            title={<Box sx={sxTitle}><GroupIcon imgSrc={icon} />{memberGroup.name}</Box>}
            subtitle={`${T("mng_member_groups_users")}`}
            open={true}
            buttonLabel={T("mng_back")}
            onClose={() => setModalType("Details")}
            onModalClose={onClose}
            sx={{ ...styles.container.base, ...styles.container.users }}
          >
            <MemberGroupUsers member_group={memberGroup} member_group_users={memberGroupUsers} />
          </Modal>
        );
      }
      case "Users": {
        return (
          <Modal
            title={<Box sx={sxTitle}><GroupIcon imgSrc={icon} />{memberGroup.name}</Box>}
            subtitle={`${T("mng_member_groups_users")}`}
            open={true}
            onClose={onClose}
            sx={{ ...styles.container.base, ...styles.container.users }}
          >
            <MemberGroupUsers member_group={memberGroup} member_group_users={memberGroupUsers} />
          </Modal>
        );
      }
      default: {
        return (<></>);
      }
    }
  }

  return (
    <>
      <LoadingModal visible={isLoading}/>
      {modal()}
    </>
  );
}

const styles = {
  container: {
    base: {
      maxHeight: "90%"
    },
    info: {
      maxWidth: "85%",
      paddingX: 0,
      paddingBottom: 0
    },
    users: {
      maxWidth: "65%"
    }
  }
}